

import { useLocation } from "react-router-dom";
import { Menu, X, LogOut, Settings, User, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

const pageTitles: Record<string, { title: string; sub: string }> = {
  "/": { title: "Dashboard", sub: "Overview & KPIs" },
  "/enquiries": { title: "Enquiries", sub: "Manage incoming client enquiries" },
  "/quotations": { title: "Quotations", sub: "Create & manage quotations" },
  "/events": { title: "Events", sub: "Full event lifecycle management" },
  "/invoices": { title: "Invoices", sub: "Billing & payment tracking" },
  "/vendors": { title: "Vendors", sub: "External vendor management" },
  "/services": { title: "Services", sub: "Manage your services and pricing" },
  "/tasks": { title: "Tasks", sub: "Event task assignments" },
  "/rooms": { title: "Rooms", sub: "Room & venue inventory" },
  "/packages": { title: "Packages", sub: "Event packages & bundles" },
  "/staff": { title: "Staff", sub: "User accounts & roles" },
  "/reports": { title: "Reports", sub: "Analytics & insights" },
  "/settings": { title: "Settings", sub: "Banquet hall configuration" },
  "/galleries": { title: "Photo Galleries", sub: "Manage website photo galleries" },
  "/features": { title: "Features", sub: "Manage website features section" },
  "/faq": { title: "FAQ", sub: "Manage frequently asked questions" },
  "/instagram-reels": { title: "Instagram Reels", sub: "Manage Instagram reels showcase" },
};

interface TopbarProps {
  onMenuClick?: () => void;
  isSidebarOpen?: boolean;
  onAccountSettings?: () => void;
  onOpenCommandPalette?: () => void;
}

export default function Topbar({ onMenuClick, isSidebarOpen, onAccountSettings, onOpenCommandPalette }: TopbarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const info = pageTitles[pathname] || pageTitles["/"];
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    window.location.href = "/login";
  };

  return (
    <header className="bg-white border-b border-stone-200 px-4 lg:px-6 py-3 flex items-center gap-3 flex-shrink-0">
      {/* Hamburger Menu - Mobile Only */}
      <button 
        onClick={onMenuClick}
        className="lg:hidden p-2 -ml-2 rounded-xl text-stone-600 hover:bg-stone-100 transition-colors"
        aria-label="Toggle menu"
      >
        {isSidebarOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <Menu className="w-5 h-5" />
        )}
      </button>

      {/* Title - Desktop */}
      <div className="hidden lg:block">
        <h1 className="text-xl font-bold text-stone-800 font-serif">{info.title}</h1>
        <p className="text-xs text-stone-400">{info.sub}</p>
      </div>

      {/* Title - Mobile */}
      <div className="lg:hidden flex-1 text-center">
        <h1 className="text-base font-bold text-stone-800 font-serif">{info.title}</h1>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2 ml-auto">
        {/* Command Palette Trigger */}
        <button
          onClick={onOpenCommandPalette}
          className="hidden sm:flex items-center gap-2 bg-stone-100 hover:bg-stone-200 rounded-xl px-3 py-2 transition-colors group"
        >
          <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="text-sm text-stone-400 w-28 text-left">Search…</span>
          <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-mono text-stone-400 border border-stone-300 bg-white">
            ⌘K
          </kbd>
        </button>
        {/* Mobile search icon */}
        <button
          onClick={onOpenCommandPalette}
          className="sm:hidden p-2 rounded-xl text-stone-500 hover:bg-stone-100 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>

        {/* Notifications */}
        <button className="relative p-2.5 rounded-xl text-stone-600 hover:bg-stone-100">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
          <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-amber-500"></span>
        </button>

        {/* Profile Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-stone-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold gold-gradient">
              {user?.name?.charAt(0) || "A"}
            </div>
            <ChevronDown className={`w-4 h-4 text-stone-500 transition-transform ${showDropdown ? "rotate-180" : ""}`} />
          </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-stone-200 shadow-lg py-1 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
              {/* User Info Header */}
              <div className="px-4 py-3 border-b border-stone-100">
                <p className="text-sm font-semibold text-stone-800 truncate">{user?.name || "User"}</p>
                <p className="text-xs text-stone-500 truncate">{user?.email || ""}</p>
                <p className="text-xs text-amber-600 capitalize mt-1">{user?.role || ""}</p>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    onAccountSettings?.();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-stone-600 hover:bg-stone-50 transition-colors"
                >
                  <User className="w-4 h-4" />
                  Account Settings
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-stone-100 py-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
